import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import './ServiceDetail.css';

// ── Full service content data ──────────────────────────────────────────
const SERVICE_DATA = {

  // ── PCB & Hardware ────────────────────────────────────────────────────

  'ate-board-design': {
    title: 'ATE Board Design',
    short: 'Automatic Test Equipment',
    icon: '⚡',
    color: '#2563a8',
    banner: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1920&q=85&auto=format&fit=crop',
    tagline: 'World-Class ATE & Probe Card Design',
    overview: `DWB Tech Solution is one of the very few PCB design companies in India specialising in ATE (Automatic Test Equipment) boards, Probe Cards, and Application Boards. Our engineers bring decades of combined experience in designing test-grade boards that meet the most demanding semiconductor validation requirements.`,
    highlights: [
      'Expertise in Load Boards, Probe Cards, and Interface Boards',
      'High pin-count designs up to 10,000+ I/Os',
      'Signal integrity optimisation for multi-GHz test environments',
      'Compatible with Teradyne, Advantest, and Credence platforms',
      'IPC Class 3 quality standard compliance',
      'Fast turnaround prototype-to-production support',
    ],
    process: [
      { step: '01', title: 'Requirement Analysis', desc: 'Deep-dive into DUT specifications, test platform constraints and pin mapping.' },
      { step: '02', title: 'Schematic Design', desc: 'High-fidelity schematic capture with full DRC and cross-probe verification.' },
      { step: '03', title: 'Layout & Routing', desc: 'Controlled impedance routing, via optimization, and thermal management.' },
      { step: '04', title: 'SI / PI Simulation', desc: 'Pre- and post-layout simulation to guarantee first-pass success.' },
      { step: '05', title: 'Fabrication Support', desc: 'Gerber generation, DFM review, and liaison with certified fab partners.' },
    ],
    whyUs: 'DWB Tech is recognized as a leading vendor for Test Board Unit (TBU) design with a strong industry background and a client retention rate of 98%. Our 24/7 support model ensures your production schedules are never compromised.',
  },

  'probe-card': {
    title: 'Probe Card',
    short: 'Wafer-Level Test Probing',
    icon: '🔍',
    color: '#2563a8',
    banner: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Precision Probe Card Design for Wafer-Level Testing',
    overview: `DWB Tech designs cantilever, vertical, and MEMS probe cards for advanced semiconductor nodes. Our probe card designs deliver reliable wafer-level contact across millions of touchdowns in high-throughput production environments. We are one of the very few PCB design companies in India with dedicated probe card expertise.`,
    highlights: [
      'Cantilever, vertical, and MEMS probe technologies',
      'Fine-pitch probing down to 40μm pad pitch',
      'High pin-count designs up to 10,000+ probes',
      'Compatible with all major prober platforms',
      'Tungsten and palladium alloy probe materials',
      'IPC Class 3 quality standard compliance',
    ],
    process: [
      { step: '01', title: 'DUT Analysis', desc: 'Wafer pad layout, pitch, and test frequency requirements review.' },
      { step: '02', title: 'Technology Selection', desc: 'Probe technology selection based on pitch, current, and cycle requirements.' },
      { step: '03', title: 'Design & Simulation', desc: 'Mechanical and electrical simulation for contact force and signal integrity.' },
      { step: '04', title: 'Fabrication', desc: 'Precision probe card fabrication with cleanroom assembly.' },
      { step: '05', title: 'Qualification', desc: 'Electrical verification and cycle life qualification testing.' },
    ],
    whyUs: 'DWB Tech is one of the very few PCB design companies in India specialising in probe card design. Our co-design expertise across ATE boards and probe cards ensures perfect system-level integration.',
  },

  'hsd-hdi-pcb-design': {
    title: 'HSD & HDI PCB Designs',
    short: 'High-Speed & High-Density Interconnect',
    icon: '🔬',
    color: '#0ea5e9',
    banner: 'https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?w=1920&q=85&auto=format&fit=crop',
    tagline: 'High-Speed & High-Density PCB Design Excellence',
    overview: `DWB Tech delivers DDR5, PCIe Gen 5, 100GbE, and microvia HDI designs with controlled impedance routing and full SI/PI simulation for first-pass success. Our engineers handle the most complex multi-layer stackups with precision — from fine-pitch BGA fanout to any-layer HDI via structures.`,
    highlights: [
      'DDR4/DDR5, PCIe Gen 4/5, USB 3.x, 100GbE routing',
      'Microvia, blind via, and buried via HDI design',
      'Controlled impedance: single-ended and differential pair',
      'Via-in-pad with filled and capped vias',
      'Fine-pitch BGA fanout down to 0.3mm pitch',
      'Pre- and post-layout SI/PI simulation',
    ],
    process: [
      { step: '01', title: 'Stackup Definition', desc: 'Layer count, via structure, and impedance target planning.' },
      { step: '02', title: 'Pre-Layout SI', desc: 'IBIS/SPICE simulation to validate design margins before routing.' },
      { step: '03', title: 'Constraint-Driven Layout', desc: 'Rule-driven routing in proprietary constraint-driven layout environment.' },
      { step: '04', title: 'Post-Layout Extraction', desc: 'Full-board SI verification with comprehensive extraction analysis' },
      { step: '05', title: 'DFM & Handoff', desc: 'DFM review and ODB++ or Gerber X2 package delivery.' },
    ],
    whyUs: 'Our engineers have delivered PCBs running at 112 Gbps SerDes speeds for data centre and telecom applications. We combine cutting-edge simulation with hands-on fabrication knowledge for guaranteed first-pass success.',
  },

  'mlo-mlc-substrate-design': {
    title: 'MLO/MLC Substrate Design',
    short: 'Multi-Layer Organic & Ceramic Substrates',
    icon: '🧱',
    color: '#2563a8',
    banner: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Advanced Substrate Design for Chip-Scale Packaging',
    overview: `DWB Tech designs advanced MLO and MLC substrates for chip-scale packaging, RF modules, and high-frequency semiconductor applications. Our substrate design expertise bridges the gap between chip and board — delivering substrates that are electrically optimised, thermally managed, and fabrication-ready.`,
    highlights: [
      'Multi-layer organic (MLO) substrate design',
      'Multi-layer ceramic (MLC) substrate design',
      'Flip-chip and wire-bond interconnect support',
      'Sub-10μm trace and space capability',
      'RF and microwave substrate optimisation',
      'Thermal via and heat spreader integration',
    ],
    process: [
      { step: '01', title: 'Package Specification', desc: 'Die size, I/O count, frequency, and thermal requirements.' },
      { step: '02', title: 'Stackup Design', desc: 'Dielectric selection and layer stackup for target performance.' },
      { step: '03', title: 'Layout & Routing', desc: 'Fine-pitch escape routing and signal integrity optimisation.' },
      { step: '04', title: 'Simulation', desc: 'Electrical and thermal simulation for performance verification.' },
      { step: '05', title: 'Fab Handoff', desc: 'Substrate-specific DFM and fabrication package generation.' },
    ],
    whyUs: 'DWB Tech brings PCB and ASIC design expertise together for substrate design — ensuring the substrate is optimised for both the chip it carries and the board it connects to.',
  },

  'eda-conversion': {
    title: 'EDA Conversion',
    short: 'Tool Migration',
    icon: '🔄',
    color: '#7c3aed',
    banner: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Move Platforms Without Losing Fidelity',
    overview: `Migrating PCB designs between EDA platforms is notoriously error-prone. DWB Tech's EDA conversion service delivers verified, production-ready databases in your target tool — with every net, attribute, and constraint preserved.`,
    highlights: [
      'Cross-platform PCB database conversion and migration',
      ' Full-format design conversion across proprietary and open-source ecosystems',
      'Open-source design migration support',
      'Library translation with symbol, footprint, and 3D model',
      'Constraint and rule migration (impedance, length match)',
      'Batch conversion support for large design databases',
    ],
    process: [
      { step: '01', title: 'Audit', desc: 'Source database audit for completeness, design rules, and library dependencies.' },
      { step: '02', title: 'Translation', desc: 'Tool-to-tool conversion using certified translators and custom scripts.' },
      { step: '03', title: 'Verification', desc: 'Net-by-net comparison and constraint verification against source.' },
      { step: '04', title: 'Library Rebuild', desc: 'Target-tool library creation or mapping for all components.' },
      { step: '05', title: 'Acceptance', desc: 'Customer walkthrough, red-line resolution, and formal acceptance.' },
    ],
    whyUs: 'DWB Tech has successfully migrated over 10,000 designs across EDA platforms. Our structured verification process guarantees net-list fidelity and constraint accuracy — backed by a written guarantee.',
  },

  'rf-design': {
    title: 'RF Design',
    short: 'Radio Frequency Engineering',
    icon: '📡',
    color: '#2563a8',
    banner: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=1920&q=85&auto=format&fit=crop',
    tagline: 'High-Frequency Performance, Engineered to Perfection',
    overview: `RF PCB design is a specialised discipline where substrate selection, trace geometry, and component placement interact in ways that standard design rules don't capture. DWB Tech's RF team brings deep RF/microwave expertise to antenna boards, radar front-ends, and wireless communication modules.`,
    highlights: [
      'Frequency range: DC to 77 GHz',
      'Rogers, PTFE, and hybrid laminate stackup design',
      'Microstrip, stripline, and coplanar waveguide structures',
      'Antenna design and array layout',
      'RF shielding and isolation strategies',
      'EM simulation with HFSS / ADS / CST',
    ],
    process: [
      { step: '01', title: 'RF Architecture', desc: 'System link budget, frequency plan, and block diagram review.' },
      { step: '02', title: 'EM Simulation', desc: 'Full-wave EM simulation of critical passives, feeds, and transitions.' },
      { step: '03', title: 'Substrate Selection', desc: 'Laminate characterisation and stackup design for target frequency.' },
      { step: '04', title: 'Layout & Tuning', desc: 'Precision layout with stub tuning and matching network optimisation.' },
      { step: '05', title: 'Prototype & Test', desc: 'Prototype fabrication support and test fixture design.' },
    ],
    whyUs: 'From 5G mmWave antenna arrays to automotive 77 GHz radar modules, DWB Tech has delivered RF boards across the full frequency spectrum. Our EM simulation-first approach eliminates guesswork and ensures production-ready performance.',
  },

  'library-development': {
    title: 'Library Development',
    short: 'Component Libraries & Footprints',
    icon: '📚',
    color: '#06b6d4',
    banner: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1920&q=85&auto=format&fit=crop',
    tagline: 'State-of-the-Art Footprint Precision',
    overview: `A robust component library is the foundation of every reliable PCB design. DWB Tech's library engineering team creates IPC-7351-compliant symbols, footprints, and 3D models for any EDA platform — delivered with zero-defect guarantee.`,
    highlights: [
      'IPC-7351 compliant footprint generation',
      'Schematic symbols with full pin attributes and properties',
      'STEP / IDF 3D asset integration',
      'Support for  Multi-environment library compatibility and deployment',
      'Bulk library migration between enterprise-grade PCB development workflows',
      'Ongoing library maintenance and version control',
    ],
    process: [
      { step: '01', title: 'Component Sourcing', desc: 'Datasheet acquisition, dimensional verification, and tolerance analysis.' },
      { step: '02', title: 'Symbol Creation', desc: 'Logical symbol authoring with pin numbering, types, and attributes.' },
      { step: '03', title: 'Footprint Generation', desc: 'IPC-7351 land pattern calculation with courtyard, fab, and silkscreen layers.' },
      { step: '04', title: '3D Model Attachment', desc: 'STEP model sourcing or creation and alignment to footprint origin.' },
      { step: '05', title: 'QA & Delivery', desc: 'Multi-stage DRC check, peer review, and delivery in target EDA format.' },
    ],
    whyUs: 'With over 500,000 components in our internal library and a dedicated team of library engineers, DWB Tech can turn around production-ready parts in as little as 24 hours. Every part is double-checked against manufacturer drawings before delivery.',
  },

  'dfm-dft-analysis': {
    title: 'DFM / DFT Analysis',
    short: 'Design For Manufacturing & Test',
    icon: '🤖',
    color: '#06b6d4',
    banner: 'https://images.unsplash.com/photo-1617788138017-80ad40651399?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Reimagine the Way You Automate DFM',
    overview: `Manufacturing defects discovered after fabrication are expensive. DWB Tech's DFM/DFT service combines AI-assisted automated checks with expert manual review to ensure your design is producible, testable, and reliable — before a single board is made.`,
    highlights: [
      'Automated DFM check against 200+ manufacturing rules',
      'Minimum annular ring, acid trap, and solder mask sliver checks',
      'Panelisation design and pick-and-place optimisation',
      'Solder paste aperture review and stencil design',
      'ICT and flying probe test point coverage analysis',
      'BGA pad relief and rework accessibility review',
    ],
    process: [
      { step: '01', title: 'Rule Setup', desc: 'Fabricator DFM rule import and technology definition configuration.' },
      { step: '02', title: 'Auto DFM Run', desc: 'Automated DFM check execution with violation categorisation.' },
      { step: '03', title: 'Manual Review', desc: 'Expert engineer review of all flagged and borderline items.' },
      { step: '04', title: 'Fix Implementation', desc: 'Design corrections applied in native EDA tool with change log.' },
      { step: '05', title: 'DFM Report', desc: 'Comprehensive DFM report with before/after screenshots and sign-off.' },
    ],
    whyUs: "DWB Tech's DFM/DFT service has a zero field-escape rate on manufacturing defects. Our dual automated-plus-manual approach catches issues that pure rule checkers miss every time.",
  },

  'procurement-assembly': {
    title: 'Procurement & Assembly',
    short: 'End-to-End Supply Chain',
    icon: '🏭',
    color: '#059669',
    banner: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1920&q=85&auto=format&fit=crop',
    tagline: 'From BOM to Boxed Product',
    overview: `DWB Tech's procurement, fabrication, and assembly service closes the loop from design to delivery. We manage your complete supply chain — sourcing components, liaising with certified PCB fabricators, and overseeing board assembly — so you receive tested, box-ready product.`,
    highlights: [
      'Global component sourcing from authorised distributors',
      'BOM optimisation and alternate component selection',
      'IPC Class 2 and Class 3 certified PCB fabrication partners',
      'SMT, through-hole, and mixed-technology assembly',
      'AOI, X-ray, and functional test',
      'Conformal coating, potting, and ruggedisation',
    ],
    process: [
      { step: '01', title: 'BOM Review', desc: 'BOM audit, lifecycle check, and approved alternate identification.' },
      { step: '02', title: 'Sourcing', desc: 'Component procurement from franchised distributors with CoC.' },
      { step: '03', title: 'Fabrication', desc: 'PCB fabrication with IPC Class 3 inspection and test coupons.' },
      { step: '04', title: 'Assembly', desc: 'SMT and through-hole assembly with solder paste inspection and reflow profiling.' },
      { step: '05', title: 'Test & Ship', desc: 'Functional test, conformal coat (if required), and delivery with test report.' },
    ],
    whyUs: 'From prototype quantities of 1 to production runs of 10,000+, DWB Tech manages your supply chain with the same quality standards. Our single-point-of-contact model means faster delivery and clear accountability.',
  },

  'signal-power-integrity': {
    title: 'Signal & Power Integrity',
    short: 'SI / PI Analysis',
    icon: '📊',
    color: '#0ea5e9',
    banner: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Reshape Your First-Turn Success Rate',
    overview: `Poor signal and power integrity is the leading cause of costly PCB re-spins. DWB Tech's SI/PI team identifies and eliminates these issues before fabrication using industry-leading simulation tools and a structured analysis methodology.`,
    highlights: [
      'Pre- and post-layout signal integrity simulation',
      'Power distribution network (PDN) analysis and optimisation',
      'DDR, SerDes, and high-speed bus eye diagram analysis',
      'EMI pre-compliance scanning and radiated emission prediction',
      'Thermal analysis and hot-spot identification',
      'Stackup and material recommendation for target impedance',
    ],
    process: [
      { step: '01', title: 'Stackup Review', desc: 'Layer stackup analysis for impedance targets and crosstalk budget.' },
      { step: '02', title: 'Pre-Layout SI', desc: 'Topology simulation to validate source termination and receiver margins.' },
      { step: '03', title: 'PDN Design', desc: 'Decoupling capacitor selection, placement optimisation, and VRM modelling.' },
      { step: '04', title: 'Post-Layout Analysis', desc: 'S-parameter extraction and full-channel simulation with real parasitics.' },
      { step: '05', title: 'Report & Recommendations', desc: 'Detailed SI/PI report with pass/fail criteria and fix recommendations.' },
    ],
    whyUs: 'Our SI/PI consultants have resolved signal integrity issues for Fortune 500 companies in networking, storage, and computing. We pay for ourselves in re-spin savings alone.',
  },

  'cam-validation-editing': {
    title: 'CAM Validation and Editing',
    short: 'CAM Data Review & Correction',
    icon: '🛠️',
    color: '#06b6d4',
    banner: 'https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Fabrication-Ready CAM Data, Every Time',
    overview: `CAM errors discovered at the fabricator cause costly delays. DWB Tech's CAM validation service reviews Gerber, ODB++, and IPC-2581 data against fabrication rules before submission — catching errors that EDA DRC misses and ensuring your design reaches production without delays.`,
    highlights: [
      'Gerber, ODB++, and IPC-2581 format support',
      'Netlist extraction and net-by-net comparison',
      'Drill file validation and back-drill verification',
      'Impedance layer and stackup verification',
      'Panelisation design and step-and-repeat editing',
      'Fab-specific rule check and correction',
    ],
    process: [
      { step: '01', title: 'Data Import', desc: 'Gerber / ODB++ import and layer mapping verification.' },
      { step: '02', title: 'Rule Check', desc: 'Fabricator-specific DFM rule execution and violation report.' },
      { step: '03', title: 'Netlist Verification', desc: 'CAM netlist extraction and comparison against source netlist.' },
      { step: '04', title: 'Editing & Correction', desc: 'CAM-level corrections applied with full change log.' },
      { step: '05', title: 'Delivery', desc: 'Corrected CAM package delivery with validation report.' },
    ],
    whyUs: 'DWB Tech CAM engineers have reviewed thousands of designs across leading fabricators worldwide. Our validation catches errors that standard EDA DRC misses — preventing costly re-spins and fabrication delays.',
  },

  // ── Chip & ASIC ───────────────────────────────────────────────────────

  'asic-design-flow': {
    title: 'ASIC Design Flow',
    short: 'Chip-Level Design',
    icon: '🧠',
    color: '#7c3aed',
    banner: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=1920&q=85&auto=format&fit=crop',
    tagline: 'The World Sees a Chip, We See Anatomy',
    overview: `DWB Tech offers comprehensive ASIC design services from RTL specification through GDSII tapeout. Our flow integrates industry-standard tools with rigorous verification to deliver silicon that works on the first spin.`,
    highlights: [
      'RTL design in SystemVerilog / VHDL',
      'Logic synthesis with Design Compiler / Genus',
      'Static Timing Analysis (STA) with PrimeTime',
      'Floorplanning, place-and-route with Innovus / ICC2',
      'Power analysis and multi-voltage domain design',
      'DRC / LVS / ERC sign-off',
    ],
    process: [
      { step: '01', title: 'Architecture Definition', desc: 'Microarchitecture review, block partitioning, and interface specification.' },
      { step: '02', title: 'RTL Coding & Lint', desc: 'RTL authoring with SpyGlass lint and CDC analysis.' },
      { step: '03', title: 'Synthesis & STA', desc: "Timing-closure synthesis targeting the customer's PDK and frequency targets." },
      { step: '04', title: 'PnR & Signoff', desc: 'Full-chip place-and-route, parasitics extraction, and IR-drop analysis.' },
      { step: '05', title: 'Tapeout', desc: 'GDSII generation, final DRC/LVS, and foundry submission support.' },
    ],
    whyUs: 'Our ASIC team has experience across TSMC, GlobalFoundries, and Samsung nodes from 180nm to 5nm. We treat every ASIC engagement as a true partnership — from concept through silicon bring-up.',
  },

  'verification-services': {
    title: 'Verification Services',
    short: 'Agile Functional Verification',
    icon: '✅',
    color: '#059669',
    banner: 'https://images.unsplash.com/photo-1576086213369-97a306d36557?w=1920&q=85&auto=format&fit=crop',
    tagline: 'An Agile Way Forward in Verification',
    overview: `Verification is the last line of defence before silicon. DWB Tech's verification engineers build UVM-compliant testbenches that deliver measurable coverage closure and bug-free designs. We work alongside your design team or own the full verification plan independently.`,
    highlights: [
      'UVM/OVM methodology-based testbench development',
      'Functional coverage closure to customer-defined targets',
      'Formal property verification (Jasper, VC Formal)',
      'Simulation on VCS, Questa, Xcelium platforms',
      'Protocol verification: AXI, AHB, PCIe, USB, MIPI',
      'Regression automation and CI/CD integration',
    ],
    process: [
      { step: '01', title: 'Verification Plan', desc: 'Coverage model definition, testbench architecture, and resource planning.' },
      { step: '02', title: 'TB Development', desc: 'UVM agent, scoreboard, coverage collector and sequence development.' },
      { step: '03', title: 'Directed Testing', desc: 'Corner-case directed tests targeting known risk areas.' },
      { step: '04', title: 'Coverage Closure', desc: 'Constrained-random regression with functional and code coverage analysis.' },
      { step: '05', title: 'Signoff Report', desc: 'Formal coverage closure report and regression summary delivery.' },
    ],
    whyUs: 'DWB Tech has verified designs ranging from simple peripherals to complex SoCs with hundreds of IPs. Our agile verification sprints give management real-time visibility into coverage progress and risk.',
  },

  // ── Mechanical Division ───────────────────────────────────────────────

  'turn-key-solution': {
    title: 'Turn Key Solution Provider',
    short: 'Complete Mechanical Systems',
    icon: '🔑',
    color: '#0f766e',
    banner: 'https://images.unsplash.com/photo-1565043666747-69f6646db940?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Complete Mechanical Systems, Delivered',
    overview: `DWB Tech's Mechanical Division offers fully integrated turn-key solutions — from initial concept and CAD design through precision manufacturing, assembly, and on-site commissioning. We serve semiconductor equipment OEMs, defence, and industrial automation companies that demand flawless mechanical execution alongside their electronic systems.`,
    highlights: [
      'Concept design and 3D CAD modelling (SolidWorks / CATIA)',
      'Precision CNC machining, sheet metal, and fabrication',
      'Surface treatment: anodising, plating, powder coating',
      'Sub-assembly and full system integration',
      'On-site installation and commissioning support',
      'AS9100 / ISO 9001 quality management compliance',
    ],
    process: [
      { step: '01', title: 'Requirement Capture', desc: 'Detailed specification review, design constraints, and interface mapping.' },
      { step: '02', title: '3D CAD Design', desc: 'SolidWorks or CATIA modelling with tolerance analysis and DFM review.' },
      { step: '03', title: 'Prototyping', desc: 'Rapid prototype fabrication for form, fit, and function validation.' },
      { step: '04', title: 'Production', desc: 'CNC machining, welding, and assembly in ISO-certified facilities.' },
      { step: '05', title: 'Commissioning', desc: 'Final inspection, customer acceptance testing, and on-site commissioning.' },
    ],
    whyUs: 'DWB Tech delivers mechanical systems that are designed alongside the electronics — resulting in perfectly integrated assemblies with no interface surprises. Our in-house design and manufacturing capability means faster delivery and single-point accountability.',
  },

  'wafer-cassette': {
    title: 'Wafer Cassette',
    short: 'Semiconductor Wafer Handling',
    icon: '💿',
    color: '#0f766e',
    banner: 'https://images.unsplash.com/photo-1569852573858-03c9e2877792?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Precision Wafer Handling for Cleanroom Environments',
    overview: `DWB Tech designs and manufactures precision wafer cassettes and transport carriers for 200mm and 300mm wafers. Built to SEMI standards and compatible with FOUP-based automation, our cassettes are engineered to prevent contamination, particle generation, and wafer damage in the most demanding cleanroom environments.`,
    highlights: [
      '200mm and 300mm wafer cassette design',
      'FOUP and FOSB compatible carrier solutions',
      'ESD-safe and cleanroom-compatible materials',
      'SEMI E1.9, E47.1, and E15.1 standards compliance',
      'Custom slot pitch and capacity configurations',
      'Particle generation testing per SEMI standards',
    ],
    process: [
      { step: '01', title: 'Specification', desc: 'Wafer size, automation interface, environment, and handling requirements.' },
      { step: '02', title: 'Material Selection', desc: 'Polymer selection for ESD, chemical compatibility, and cleanroom class.' },
      { step: '03', title: 'Precision Machining', desc: 'CNC machining with sub-micron tolerance for slot alignment and flatness.' },
      { step: '04', title: 'Cleanliness Validation', desc: 'Particle count testing and outgassing analysis per SEMI standards.' },
      { step: '05', title: 'Qualification', desc: 'Customer qualification testing and production release.' },
    ],
    whyUs: 'Our wafer cassettes are used in leading semiconductor fabs and equipment OEM facilities worldwide. DWB Tech combines materials expertise with precision machining to deliver cassettes that protect wafers at every handling step.',
  },

  'pogo-tower': {
    title: 'Pogo Tower',
    short: 'Spring Pin Contact Systems',
    icon: '🏗️',
    color: '#0f766e',
    banner: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Reliable Electrical Contact for Every Test Cycle',
    overview: `Pogo towers are the critical mechanical and electrical interface between ATE load boards and the device under test. DWB Tech engineers high-density pogo pin tower assemblies that deliver repeatable, low-resistance contact across millions of test cycles — for semiconductor handlers, burn-in boards, and wafer probing applications.`,
    highlights: [
      'High-density pogo pin layouts up to 2,000+ contacts',
      'Custom pitch: 0.5mm, 1.0mm, 1.27mm, 2.54mm',
      'Spring force and stroke optimisation for DUT protection',
      'Gold-plated contacts for low resistance and longevity',
      'Alignment pin and guide feature integration',
      'Compatible with Teradyne, Advantest, and Xcerra handlers',
    ],
    process: [
      { step: '01', title: 'DUT Analysis', desc: 'Device pad layout, force requirements, and electrical specification review.' },
      { step: '02', title: 'Pin Selection', desc: 'Pogo pin type, spring force, travel, and plating specification.' },
      { step: '03', title: 'Tower Design', desc: 'Precision tower body design with alignment features and PCB interface.' },
      { step: '04', title: 'Assembly', desc: 'Clean-room assembly with 100% contact resistance verification.' },
      { step: '05', title: 'Cycle Testing', desc: 'Life-cycle testing to specified insertion count with pass/fail report.' },
    ],
    whyUs: 'DWB Tech pogo towers are designed by engineers who also design the ATE boards they interface with. This co-design advantage ensures perfect mechanical and electrical integration — eliminating the most common cause of test failures.',
  },

  'interface': {
    title: 'Interface',
    short: 'Test Interface Assemblies',
    icon: '🔌',
    color: '#0f766e',
    banner: 'https://images.unsplash.com/photo-1601933470096-0e34634ffcde?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Precision Interface Solutions for ATE Systems',
    overview: `The interface assembly is the physical bridge between the ATE system and the device under test. DWB Tech designs and manufactures custom interface boards, docking hardware, and mechanical interface assemblies that deliver precise alignment, reliable electrical contact, and long service life in high-throughput production test environments.`,
    highlights: [
      'Custom DUT interface board design and fabrication',
      'Mechanical docking and alignment guide systems',
      'Kelvin contact and guarded measurement interfaces',
      'High-frequency SMA, SMP, and micro-coax integration',
      'Handler-specific interface kit design',
      'ESD protection and thermal management features',
    ],
    process: [
      { step: '01', title: 'ATE & Handler Mapping', desc: 'Test platform, handler, and DUT interface specification review.' },
      { step: '02', title: 'Electrical Design', desc: 'Signal routing, impedance control, and contact interface design.' },
      { step: '03', title: 'Mechanical Design', desc: 'Docking features, alignment pins, and clamping mechanism design.' },
      { step: '04', title: 'Build & Verify', desc: 'Fabrication, assembly, and electrical verification on ATE platform.' },
      { step: '05', title: 'Production Support', desc: 'Spare parts management, refurbishment, and field support.' },
    ],
    whyUs: 'With deep expertise in both PCB design and mechanical engineering, DWB Tech delivers interface solutions that are optimised across both domains simultaneously — reducing test contact resistance, improving yield, and extending kit life.',
  },

  'hinge-manipulator-cart': {
    title: 'Hinge, Manipulator & Cart',
    short: 'Test Head Handling Equipment',
    icon: '🦾',
    color: '#0f766e',
    banner: 'https://images.unsplash.com/photo-1565043666747-69f6646db940?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Ergonomic Test Head Handling, Engineered for Safety',
    overview: `Test head manipulators, docking hinges, and equipment carts are essential for safe, efficient ATE cell operation. DWB Tech designs and manufactures ergonomic handling equipment that reduces operator fatigue, eliminates manual lifting injury risk, and improves test cell throughput in semiconductor manufacturing environments.`,
    highlights: [
      'Test head manipulators for 50kg to 800kg payloads',
      'Motorised and manual docking hinge systems',
      'Precision docking repeatability within ±0.1mm',
      'Equipment carts with height adjustment and locking casters',
      'CE and UL safety compliance',
      'Compatible with Teradyne, Advantest, Cohu, and Xcerra ATE',
    ],
    process: [
      { step: '01', title: 'Load Analysis', desc: 'Test head weight, CoG, and docking force requirements analysis.' },
      { step: '02', title: 'Mechanical Design', desc: 'Structural FEA, kinematic analysis, and ergonomic design.' },
      { step: '03', title: 'Safety Review', desc: 'FMEA, safety circuit design, and CE/UL compliance review.' },
      { step: '04', title: 'Fabrication', desc: 'Steel and aluminium fabrication, painting, and assembly.' },
      { step: '05', title: 'Acceptance Test', desc: 'Load testing, docking repeatability verification, and customer acceptance.' },
    ],
    whyUs: "DWB Tech manipulators are designed by engineers who understand ATE systems end-to-end. Our handling equipment is engineered to dock precisely with our interface and pogo tower products — providing a fully integrated test cell solution from a single supplier.",
  },

  'test-head-chassis': {
    title: 'Test Head Chassis Design & Mfg',
    short: 'Custom Test Head Enclosures',
    icon: '🖥️',
    color: '#0f766e',
    banner: 'https://images.unsplash.com/photo-1591639713936-6fafe6748de0?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Structural Precision for High-Performance Test Heads',
    overview: `The test head chassis is the structural heart of an ATE system, supporting hundreds of PCBs, power supplies, and thermal systems while maintaining signal integrity across thousands of channels. DWB Tech delivers precision-engineered test head enclosures in aluminium and steel — designed for rigidity, EMI shielding, and thermal performance.`,
    highlights: [
      'Aluminium and steel chassis design and fabrication',
      'CNC machined card guide and backplane mounting systems',
      'EMI shielding — gasketing, filtered connectors, and grounding',
      'Thermal management — forced air and liquid cooling integration',
      'Cable management and harness routing design',
      'MIL-STD-810 environmental qualification support',
    ],
    process: [
      { step: '01', title: 'Concept Design', desc: 'Chassis architecture, card count, backplane layout, and thermal budget.' },
      { step: '02', title: 'Structural Analysis', desc: 'FEA for structural rigidity, shock, and vibration compliance.' },
      { step: '03', title: 'EMI & Thermal Design', desc: 'Shielding effectiveness analysis and thermal CFD simulation.' },
      { step: '04', title: 'Precision Fabrication', desc: 'CNC machining, welding, and surface treatment to drawing.' },
      { step: '05', title: 'Integration & Test', desc: 'Board integration, system-level acceptance test, and delivery.' },
    ],
    whyUs: 'DWB Tech chassis are designed by the same engineers who populate them with ATE boards. This integrated design approach ensures every mechanical feature serves the electrical system — from connector alignment to EMI containment and thermal paths.',
  },

  'ems-mobile-assembly': {
    title: 'EMS Mobile Assembly',
    short: 'Electronics Manufacturing Services',
    icon: '📱',
    color: '#0f766e',
    banner: 'https://images.unsplash.com/photo-1565193566173-7a0ee3dbe261?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Full EMS Mobile Assembly Services',
    overview: `DWB Tech provides complete EMS mobile assembly services — SMT, through-hole, and mixed-technology board assembly for consumer electronics and industrial products with full quality traceability from component to finished board.`,
    highlights: [
      'SMT and through-hole mixed-technology assembly',
      'Fine-pitch BGA and QFN component assembly',
      'AOI, X-ray, and functional test',
      'IPC-A-610 Class 2 and Class 3 workmanship',
      'Full traceability and serialisation',
      'NPI and production volume support',
    ],
    process: [
      { step: '01', title: 'NPI Review', desc: 'Design for assembly review, stencil design, and process planning.' },
      { step: '02', title: 'Solder Paste', desc: 'Automated solder paste print with SPI verification.' },
      { step: '03', title: 'Component Placement', desc: 'High-accuracy pick-and-place with feeder verification.' },
      { step: '04', title: 'Reflow & Inspection', desc: 'Profiled reflow, AOI, and X-ray inspection.' },
      { step: '05', title: 'Test & Ship', desc: 'Functional test, conformal coat if required, and delivery with report.' },
    ],
    whyUs: 'DWB Tech EMS capability is backed by our PCB design expertise — our assembly team understands the design intent, reducing first-article failures and accelerating NPI timelines.',
  },

  'high-precision-machine-shop': {
    title: 'High Precision Machine Shop',
    short: 'CNC Machining & Fabrication',
    icon: '🏭',
    color: '#0f766e',
    banner: 'https://images.unsplash.com/photo-1565043666747-69f6646db940?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Precision CNC Machining to ±0.005mm',
    overview: `DWB Tech operates a state-of-the-art precision machine shop delivering CNC machining, turning, milling, and grinding for semiconductor, defence, and industrial applications requiring the tightest tolerances.`,
    highlights: [
      'CNC milling, turning, and grinding',
      'Tolerances to ±0.005mm',
      'Aluminium, steel, titanium, and engineering plastics',
      'Surface treatment: anodising, plating, powder coating',
      'CMM inspection with full dimensional report',
      'ISO 9001 quality management',
    ],
    process: [
      { step: '01', title: 'Drawing Review', desc: 'Engineering drawing review, tolerance analysis, and process planning.' },
      { step: '02', title: 'CNC Programming', desc: 'CAM programming with toolpath simulation and collision check.' },
      { step: '03', title: 'Machining', desc: 'Precision CNC machining with in-process measurement.' },
      { step: '04', title: 'Surface Treatment', desc: 'Anodising, plating, or coating per drawing specification.' },
      { step: '05', title: 'CMM Inspection', desc: 'Full dimensional inspection with CMM report and delivery.' },
    ],
    whyUs: 'DWB Tech machine shop output feeds directly into our mechanical assembly line — ensuring machined parts fit perfectly with ATE equipment, test fixtures, and automation systems we design and build.',
  },

  'on-site-service-support': {
    title: 'On Site Service Support (OSS)',
    short: 'Field Engineering Support',
    icon: '🛠️',
    color: '#0f766e',
    banner: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Expert Field Engineering at Your Facility',
    overview: `DWB Tech's On Site Service Support team provides dedicated field engineering for semiconductor equipment installation, maintenance, and troubleshooting — at your facility, on your schedule. Our OSS engineers bring the same expertise as our design team to every site visit.`,
    highlights: [
      'Equipment installation and commissioning',
      'Preventive and corrective maintenance',
      'ATE system troubleshooting and repair',
      'Spare parts management and kitting',
      'Engineer training and knowledge transfer',
      '24/7 emergency response support',
    ],
    process: [
      { step: '01', title: 'Site Assessment', desc: 'Facility review, equipment audit, and support plan definition.' },
      { step: '02', title: 'Mobilisation', desc: 'Engineer assignment, tools, and spare parts preparation.' },
      { step: '03', title: 'Installation', desc: 'Equipment installation, alignment, and commissioning.' },
      { step: '04', title: 'Verification', desc: 'System functional verification and customer acceptance.' },
      { step: '05', title: 'Ongoing Support', desc: 'Scheduled maintenance, emergency response, and reporting.' },
    ],
    whyUs: 'Our OSS engineers are the same team that designs and builds the equipment — giving them unmatched diagnostic depth and faster resolution of complex field issues.',
  },

  // ── Wiring Harness ────────────────────────────────────────────────────

  'semiconductor-ems': {
    title: 'Semiconductor / EMS Industries',
    short: 'Test & Handler Cable Assemblies',
    icon: '💾',
    color: '#b45309',
    banner: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1920&q=85&auto=format&fit=crop',
    tagline: 'High-Reliability Cable Assemblies for Semiconductor Test',
    overview: `DWB Tech manufactures high-density cable assemblies and interconnects specifically for semiconductor test handlers, wafer probers, burn-in systems, and EMS production environments. Built to IPC-A-620 Class 3, our assemblies deliver the signal integrity and reliability that production test demands.`,
    highlights: [
      'High-density ribbon and discrete wire harnesses',
      'IPC-A-620 Class 3 workmanship standard',
      'Controlled impedance coaxial and twinax assemblies',
      'Handler-specific cable kit design and documentation',
      'ESD-safe materials and shielding for sensitive signals',
      'Full electrical test with test report for every assembly',
    ],
    process: [
      { step: '01', title: 'Signal Analysis', desc: 'Signal type, frequency, voltage, and connector interface review.' },
      { step: '02', title: 'Cable Selection', desc: 'Wire gauge, insulation, shielding, and connector specification.' },
      { step: '03', title: 'Harness Design', desc: 'Schematic, formboard, and routing documentation.' },
      { step: '04', title: 'Assembly', desc: 'Hand-built assembly per IPC-A-620 Class 3 with process controls.' },
      { step: '05', title: 'Test & Ship', desc: 'Continuity, insulation resistance, and Hi-Pot test with report.' },
    ],
    whyUs: 'Because DWB Tech also designs the PCBs and mechanical systems in semiconductor test equipment, our cable assembly team understands the signal environment completely — delivering assemblies that are electrically optimised, not just mechanically connected.',
  },

  'automation-machine-building': {
    title: 'Automation / Machine Building',
    short: 'Industrial Cable Harnesses',
    icon: '🤖',
    color: '#b45309',
    banner: 'https://images.unsplash.com/photo-1565043666747-69f6646db940?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Rugged Harnesses Built for Industrial Environments',
    overview: `Industrial automation and machine building demand cable harnesses that survive continuous flexing, vibration, heat, and chemical exposure across years of 24/7 operation. DWB Tech manufactures rugged harnesses for CNC machinery, robotic work cells, conveyor systems, and custom automation equipment.`,
    highlights: [
      'Continuous flex cable rated for 10M+ bend cycles',
      'Servo, stepper, and encoder cable assemblies',
      'IP67 and IP69K rated connector and overmould options',
      'High-temperature insulation — FEP, PTFE, silicone',
      'CE-marked assemblies compliant with Machinery Directive',
      'Custom cable management trays and drag chain integration',
    ],
    process: [
      { step: '01', title: 'Application Review', desc: 'Duty cycle, flex requirements, environment rating, and connector interfaces.' },
      { step: '02', title: 'Cable Engineering', desc: 'Cable type selection, bend radius calculation, and lifecycle modelling.' },
      { step: '03', title: 'Design Documentation', desc: 'Full harness schematic, formboard, and assembly drawing package.' },
      { step: '04', title: 'Assembly & Overmould', desc: 'Assembly with strain relief, overmoulding, and marking.' },
      { step: '05', title: 'Test & Certify', desc: 'Electrical test, flex endurance sample testing, and CE documentation.' },
    ],
    whyUs: 'DWB Tech harnesses are engineered for the full equipment lifecycle — not just initial installation. Our team analyses the mechanical duty cycle and thermal environment to select materials that outlast the machine they are built into.',
  },

  'defense-military': {
    title: 'Defense / Military',
    short: 'MIL-Spec Wiring Harnesses',
    icon: '🛡️',
    color: '#b45309',
    banner: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=1920&q=85&auto=format&fit=crop',
    tagline: 'MIL-Spec Harnesses for Mission-Critical Systems',
    overview: `Defence and military systems demand wiring harnesses that perform flawlessly under extreme conditions — shock, vibration, wide temperature ranges, and chemical exposure. DWB Tech manufactures MIL-DTL-17 and MIL-DTL-27500 compliant harnesses for ground vehicles, avionics, naval platforms, and communications systems.`,
    highlights: [
      'MIL-DTL-17, MIL-DTL-27500, and MIL-DTL-3885 compliance',
      'MIL-DTL-38999, MIL-DTL-5015 connector assembly',
      'MIL-STD-810 shock and vibration rated',
      'EMI shielding per MIL-STD-461',
      'Coaxial assemblies for radar and communications',
      'Full traceability — material certs, test records, and first article',
    ],
    process: [
      { step: '01', title: 'Specification Review', desc: 'MIL-spec requirements, qualification class, and end-use environment.' },
      { step: '02', title: 'Approved Material Selection', desc: 'QPL-listed wire, connector, and backshell selection.' },
      { step: '03', title: 'Documentation Package', desc: 'Engineering drawing, BOM, and inspection test plan to customer standard.' },
      { step: '04', title: 'Controlled Assembly', desc: 'Assembly by certified technicians with in-process inspection.' },
      { step: '05', title: 'First Article & Production', desc: 'FAI per AS9102, production lot testing, and delivery with data package.' },
    ],
    whyUs: 'DWB Tech has supplied wiring harness assemblies to defence prime contractors and system integrators for over a decade. Our quality management system and material traceability processes are built to satisfy the most rigorous defence procurement standards.',
  },

  'aero-space': {
    title: 'Aerospace',
    short: 'Aerospace Cable Assemblies',
    icon: '✈️',
    color: '#b45309',
    banner: 'https://images.unsplash.com/photo-1436891620584-47fd0e565afb?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Lightweight, Reliable Assemblies for Aerospace',
    overview: `Aerospace wiring harnesses must be lightweight, fire-resistant, and absolutely reliable — because failures are not an option. DWB Tech manufactures aerospace cable assemblies to AS9100 quality standards, using approved materials and processes that meet the exacting requirements of commercial and defence aviation programmes.`,
    highlights: [
      'AS9100 Rev D certified quality management system',
      'Fire-resistant PTFE, FEP, and ETFE insulated wire',
      'Weight-optimised designs using aluminium and composite conductors',
      'D38999, ARINC 600, and nano-D connector assembly',
      'EMI/RFI shielding for avionics signal integrity',
      'Full first article inspection per AS9102',
    ],
    process: [
      { step: '01', title: 'Aerospace Spec Mapping', desc: 'OEM specification, DO-160 environment, and certification requirements.' },
      { step: '02', title: 'Material Approval', desc: 'Approved wire and connector selection from qualified products list.' },
      { step: '03', title: 'Design & Stress Analysis', desc: 'Routing, bend radius, weight budget, and thermal analysis.' },
      { step: '04', title: 'Certified Assembly', desc: 'Assembly in AS9100 facility with IPC-A-620 Space & Military addendum.' },
      { step: '05', title: 'FAI & Delivery', desc: 'First Article Inspection report, test data package, and conformance certificate.' },
    ],
    whyUs: 'DWB Tech combines AS9100 quality discipline with the engineering depth of a full-service electronics and mechanical design company. Our assemblies are designed to the same exacting standards as the avionics systems they connect.',
  },

  'medical': {
    title: 'Medical',
    short: 'Medical-Grade Cable Assemblies',
    icon: '🏥',
    color: '#b45309',
    banner: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Biocompatible Cable Assemblies for Medical Devices',
    overview: `Medical cable assemblies must meet the highest standards of biocompatibility, electrical safety, and sterilisation resistance. DWB Tech manufactures ISO 13485 compliant cable assemblies for diagnostic imaging equipment, surgical robots, patient monitoring systems, and implantable device manufacturing test fixtures.`,
    highlights: [
      'ISO 13485 quality management system',
      'IEC 60601-1 patient safety compliance',
      'Biocompatible materials — latex-free, DEHP-free',
      'Autoclave, EtO, and gamma sterilisation compatible options',
      'Lemo, ODU, and medical-grade circular connector assembly',
      'Traceability and device history record per 21 CFR Part 820',
    ],
    process: [
      { step: '01', title: 'Risk Analysis', desc: 'ISO 14971 risk analysis for the cable assembly and its use environment.' },
      { step: '02', title: 'Material Biocompatibility', desc: 'ISO 10993 biocompatibility review and material selection.' },
      { step: '03', title: 'Design Verification', desc: 'Electrical safety, insulation, and sterilisation validation testing.' },
      { step: '04', title: 'Controlled Production', desc: 'ISO 13485 controlled production with device history records.' },
      { step: '05', title: 'Final Inspection & Release', desc: '100% electrical test, visual inspection, and quality release documentation.' },
    ],
    whyUs: 'DWB Tech operates an ISO 13485 certified facility with a dedicated medical product line. Our team understands both the regulatory requirements and the clinical environment — delivering assemblies that are safe, reliable, and audit-ready.',
  },

  'telecom': {
    title: 'Telecom',
    short: 'High-Frequency RF & Fibre Assemblies',
    icon: '📶',
    color: '#b45309',
    banner: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Low-Loss RF Assemblies for Telecom Infrastructure',
    overview: `5G base stations, data centres, and telecom switching equipment demand cable assemblies with extremely low insertion loss, precise impedance control, and long-term reliability in outdoor environments. DWB Tech manufactures RF coaxial assemblies, fibre optic harnesses, and structured cabling solutions for leading telecom infrastructure programmes.`,
    highlights: [
      'Low-loss coaxial assemblies — N, SMA, 4.3-10, DIN 7-16 connectors',
      'Phase-matched and time-delay matched cable sets',
      'ODVA, SC, LC, and MPO fibre optic assemblies',
      'IP67 weatherproof overmoulding for outdoor installation',
      'PIM (Passive Intermodulation) tested to -150 dBc',
      'Structured cabling design and installation support',
    ],
    process: [
      { step: '01', title: 'RF Performance Spec', desc: 'Frequency, insertion loss, VSWR, and PIM specification review.' },
      { step: '02', title: 'Cable & Connector Selection', desc: 'Dielectric and conductor selection for target frequency and environment.' },
      { step: '03', title: 'Assembly', desc: 'Precision RF assembly with torque-controlled connector termination.' },
      { step: '04', title: 'VNA Verification', desc: '100% VNA test — insertion loss, return loss, and phase verification.' },
      { step: '05', title: 'PIM Test & Delivery', desc: 'PIM testing for base station applications and calibration documentation.' },
    ],
    whyUs: 'DWB Tech has supplied RF cable assemblies and fibre harnesses to 5G infrastructure programmes across Asia and the Middle East. Our RF measurement capability and outdoor-rated assembly processes make us a trusted partner for demanding telecom deployments.',
  },

  // ── SMT ───────────────────────────────────────────────────────────────

  'fully-automated-assembly': {
    title: 'Fully Automated Assembly',
    short: 'End-to-End SMT Automation',
    icon: '🤖',
    color: '#be185d',
    banner: 'https://images.unsplash.com/photo-1565193566173-7a0ee3dbe261?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Zero Manual Handling, Consistent Quality',
    overview: `DWB Tech's fully automated SMT assembly lines eliminate manual handling at every stage — solder paste inspection, pick-and-place, reflow, AOI, and X-ray — delivering consistent, traceable quality for every board produced.`,
    highlights: [
      'Automated solder paste print with SPI',
      'High-speed pick-and-place — 90,000 CPH capacity',
      'Nitrogen reflow for fine-pitch and BGA components',
      'Inline AOI after reflow',
      '3D X-ray for BGA and hidden joint inspection',
      'Full traceability with barcode/QR serialisation',
    ],
    process: [
      { step: '01', title: 'Programme Setup', desc: 'Gerber import, centroid generation, and machine programme creation.' },
      { step: '02', title: 'Paste Print', desc: 'Automated solder paste print with 100% SPI verification.' },
      { step: '03', title: 'Placement', desc: 'High-accuracy automated placement with feeder verification.' },
      { step: '04', title: 'Reflow', desc: 'Profiled nitrogen reflow with continuous thermal monitoring.' },
      { step: '05', title: 'Inspect & Test', desc: 'AOI, X-ray, and functional test with full results logging.' },
    ],
    whyUs: 'DWB Tech fully automated lines produce zero handling defects. Our process engineers monitor every parameter in real time — catching drift before it becomes a defect.',
  },

  'three-line-smt-support': {
    title: '3-Line SMT Support',
    short: 'Multi-Line Production Capacity',
    icon: '🏭',
    color: '#be185d',
    banner: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Three Lines, Uninterrupted Production',
    overview: `Three dedicated SMT production lines enable DWB Tech to run parallel programmes, fast changeovers, and uninterrupted scheduling — giving high-volume customers the capacity confidence they need to meet aggressive delivery schedules.`,
    highlights: [
      'Three fully independent SMT production lines',
      'Parallel programme capability for mixed products',
      'Fast changeover — under 30 minutes per product change',
      'Dedicated NPI line for new product introduction',
      'Line balancing and OEE monitoring',
      'Customer-dedicated line option for high-volume programmes',
    ],
    process: [
      { step: '01', title: 'Capacity Planning', desc: 'Production schedule review and line allocation planning.' },
      { step: '02', title: 'Programme Qualification', desc: 'First article on dedicated line with full inspection.' },
      { step: '03', title: 'Production Run', desc: 'Scheduled production with real-time OEE monitoring.' },
      { step: '04', title: 'Quality Audit', desc: 'In-process AOI, X-ray sampling, and quality gate checks.' },
      { step: '05', title: 'Delivery', desc: 'Final inspection, pack, and on-time delivery with quality report.' },
    ],
    whyUs: 'Three lines means no scheduling conflicts. DWB Tech customers never wait for capacity — we plan production to meet your delivery schedule, not ours.',
  },

  'smt-cph-capacity': {
    title: '90,000 CPH Capacity',
    short: 'High-Throughput Placement',
    icon: '⚡',
    color: '#be185d',
    banner: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1920&q=85&auto=format&fit=crop',
    tagline: '90,000 Components Per Hour — Built for Volume',
    overview: `DWB Tech's 90,000 CPH placement capacity supports high-mix, high-volume production with fast turnaround. Our placement machines handle 01005 passives through large connectors with equal precision and speed.`,
    highlights: [
      '90,000 CPH combined placement rate',
      'Component range: 01005 to 150mm × 150mm',
      'Fine-pitch capability to 0.3mm pitch BGA',
      'Intelligent feeder management for high-mix',
      'Real-time placement rate and yield monitoring',
      'Fast NPI turnaround — 24-hour first article',
    ],
    process: [
      { step: '01', title: 'BOM & Centroid', desc: 'BOM import, feeder mapping, and centroid file verification.' },
      { step: '02', title: 'Setup & Verify', desc: 'Feeder setup, vision calibration, and first-piece verification.' },
      { step: '03', title: 'Production', desc: 'High-speed production run with real-time CPH monitoring.' },
      { step: '04', title: 'Inspection', desc: 'Post-placement AOI and statistical sampling.' },
      { step: '05', title: 'Reporting', desc: 'Placement yield report and SPC data delivery.' },
    ],
    whyUs: 'DWB Tech 90,000 CPH capacity is matched by process discipline — high speed means nothing without high accuracy. Our placement machines achieve >99.99% placement accuracy at full speed.',
  },

  // ── IT Software Services ───────────────────────────────────────────────

  'web-app-development': {
    title: 'Web App Development',
    short: 'Custom Web Applications',
    icon: '🌐',
    color: '#2563a8',
    banner: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Powerful Web Applications, Built to Scale',
    overview: `DWB Tech builds modern, scalable web applications tailored to the needs of electronics and semiconductor companies. From internal tooling to customer-facing portals, we deliver robust, responsive applications using cutting-edge technology stacks.`,
    highlights: [
      'React, Next.js, and Vue.js frontend development',
      'Node.js, Django, and REST/GraphQL backend APIs',
      'Responsive, mobile-first UI/UX design',
      'Authentication, role-based access control',
      'Third-party EDA tool and equipment API integration',
      'Performance optimisation and SEO best practices',
    ],
    process: [
      { step: '01', title: 'Discovery', desc: 'Requirements gathering, user story mapping, and wireframing.' },
      { step: '02', title: 'UI/UX Design', desc: 'Figma prototypes, design system, and stakeholder review.' },
      { step: '03', title: 'Development', desc: 'Agile sprint delivery with CI/CD and regular demos.' },
      { step: '04', title: 'QA & Testing', desc: 'Unit, integration, and cross-browser acceptance testing.' },
      { step: '05', title: 'Deploy & Support', desc: 'Cloud deployment, monitoring, and post-launch SLA support.' },
    ],
    whyUs: 'Our developers understand electronics workflows, so integrations with EDA tools, test equipment, and MES systems come naturally. We deliver software that fits your engineering environment, not the other way around.',
  },

  'software-application-dev': {
    title: 'Software Application Dev',
    short: 'Custom Software Solutions',
    icon: '🖥️',
    color: '#7c3aed',
    banner: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Software That Speaks Your Engineering Language',
    overview: `DWB Tech develops custom software applications for electronics and semiconductor companies — from EDA automation scripts to full MES and PLM integrations. We bridge the gap between hardware workflows and modern software infrastructure.`,
    highlights: [
      'Desktop applications (Windows, Linux, macOS)',
      'EDA scripting and automation (Tcl, Python, SKILL)',
      'MES, PLM, and ERP integrations',
      'Data analytics and reporting dashboards',
      'Custom CAM and DFM automation tools',
      'Legacy system modernisation',
    ],
    process: [
      { step: '01', title: 'Scoping', desc: 'Technical requirements, architecture, and integration mapping.' },
      { step: '02', title: 'Prototype', desc: 'Rapid prototype to validate core functionality and UX.' },
      { step: '03', title: 'Development', desc: 'Iterative development with weekly builds and reviews.' },
      { step: '04', title: 'Testing', desc: 'Unit testing, integration testing, and UAT with your team.' },
      { step: '05', title: 'Handover', desc: 'Documentation, training, and post-go-live support.' },
    ],
    whyUs: 'Unlike generic software vendors, DWB Tech developers come from electronics and semiconductor backgrounds. We understand the full spectrum of industry-standard EDA, test, and manufacturing platforms — and build software that integrates with them seamlessly.',
  },

  'mobile-app-development': {
    title: 'Mobile App Development',
    short: 'iOS & Android Applications',
    icon: '📱',
    color: '#059669',
    banner: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Engineering-Grade Mobile Experiences',
    overview: `DWB Tech builds mobile applications for iOS and Android that connect field engineers, production staff, and management to real-time data. From inventory scanning apps to IoT device dashboards, we deliver mobile solutions built for industrial environments.`,
    highlights: [
      'Native iOS (Swift) and Android (Kotlin) development',
      'Cross-platform React Native and Flutter',
      'BLE, NFC, and barcode/QR scanner integration',
      'Real-time data sync with backend APIs',
      'Offline-first architecture for factory floor use',
      'Push notifications and alert management',
    ],
    process: [
      { step: '01', title: 'Discovery', desc: 'Use case mapping, device requirements, and platform selection.' },
      { step: '02', title: 'UI Design', desc: 'Mobile-first UX design with industrial usability in mind.' },
      { step: '03', title: 'Development', desc: 'Sprint-based development with TestFlight/Play Store beta builds.' },
      { step: '04', title: 'QA', desc: 'Device compatibility testing, performance profiling, and security review.' },
      { step: '05', title: 'Launch & Support', desc: 'App store submission, launch, and ongoing maintenance.' },
    ],
    whyUs: 'We build mobile apps that survive the factory floor — offline-capable, hardware-integrated, and engineered for reliability. Our domain expertise means we understand what electronics and manufacturing teams actually need.',
  },

  'cloud-app-development': {
    title: 'Cloud App Development',
    short: 'Cloud-Native Applications',
    icon: '☁️',
    color: '#0ea5e9',
    banner: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Scale Your Operations to the Cloud',
    overview: `DWB Tech architects and builds cloud-native applications on AWS, Azure, and GCP. Whether you need to move legacy design data to the cloud, build a SaaS platform, or deploy scalable microservices, our cloud team delivers secure, performant, and cost-optimised solutions.`,
    highlights: [
      'AWS, Azure, and Google Cloud Platform expertise',
      'Microservices and serverless architecture',
      'Containerisation with Docker and Kubernetes',
      'CI/CD pipeline setup and DevOps automation',
      'Cloud data storage, warehousing, and analytics',
      'Security compliance (SOC 2, ISO 27001 aligned)',
    ],
    process: [
      { step: '01', title: 'Cloud Assessment', desc: 'Current workload review and cloud migration/build strategy.' },
      { step: '02', title: 'Architecture', desc: 'Cloud-native architecture design with cost and scale modelling.' },
      { step: '03', title: 'Build', desc: 'Infrastructure-as-code, microservice development, and API layer.' },
      { step: '04', title: 'Security & Compliance', desc: 'IAM, network security, encryption, and audit logging.' },
      { step: '05', title: 'Go Live & Optimise', desc: 'Deployment, monitoring setup, and ongoing cost optimisation.' },
    ],
    whyUs: 'DWB Tech has migrated design data, test results, and MES systems to the cloud for electronics manufacturers globally. We know how to handle large binary EDA files, high-frequency test data streams, and compliance requirements specific to your industry.',
  },

  'embedded-software-dev': {
    title: 'Embedded Software Dev',
    short: 'Firmware & Embedded Systems',
    icon: '⚙️',
    color: '#d97706',
    banner: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Firmware That Brings Your Hardware to Life',
    overview: `DWB Tech's embedded software team writes the firmware that makes your hardware work. From bare-metal microcontroller code to embedded Linux BSPs, we deliver reliable, well-documented embedded software that's designed for production from day one.`,
    highlights: [
      'Bare-metal C/C++ firmware for ARM, RISC-V, and PIC',
      'RTOS development (FreeRTOS, Zephyr, ThreadX)',
      'Embedded Linux BSP and driver development',
      'Communication stacks: UART, SPI, I2C, CAN, USB, Ethernet',
      'Bootloader development and OTA update systems',
      'MISRA-C compliance for safety-critical applications',
    ],
    process: [
      { step: '01', title: 'Requirements', desc: 'Hardware interface mapping, timing requirements, and safety analysis.' },
      { step: '02', title: 'Architecture', desc: 'RTOS selection, task design, memory map, and driver architecture.' },
      { step: '03', title: 'Development', desc: 'Module-by-module firmware development with unit test coverage.' },
      { step: '04', title: 'Integration', desc: 'Hardware-in-the-loop testing and system integration debugging.' },
      { step: '05', title: 'Validation', desc: 'Final functional validation, performance profiling, and documentation.' },
    ],
    whyUs: 'Because DWB Tech designs both the hardware and the firmware, our embedded software engineers understand the board at a schematic level. This co-design advantage eliminates integration surprises and accelerates time to market.',
  },

  // ── Legacy / alias slugs ──────────────────────────────────────────────

  'asic-flow': {
    title: 'ASIC Design Flow',
    short: 'Chip-Level Design',
    icon: '🧠',
    color: '#7c3aed',
    banner: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=1920&q=85&auto=format&fit=crop',
    tagline: 'The World Sees a Chip, We See Anatomy',
    overview: `DWB Tech offers comprehensive ASIC design services from RTL specification through GDSII tapeout. Our flow integrates industry-standard tools with rigorous verification to deliver silicon that works on the first spin.`,
    highlights: [
      'RTL design in SystemVerilog / VHDL',
      'Logic synthesis with Design Compiler / Genus',
      'Static Timing Analysis (STA) with PrimeTime',
      'Floorplanning, place-and-route with Innovus / ICC2',
      'Power analysis and multi-voltage domain design',
      'DRC / LVS / ERC sign-off',
    ],
    process: [
      { step: '01', title: 'Architecture Definition', desc: 'Microarchitecture review, block partitioning, and interface specification.' },
      { step: '02', title: 'RTL Coding & Lint', desc: 'RTL authoring with SpyGlass lint and CDC analysis.' },
      { step: '03', title: 'Synthesis & STA', desc: "Timing-closure synthesis targeting the customer's PDK and frequency targets." },
      { step: '04', title: 'PnR & Signoff', desc: 'Full-chip place-and-route, parasitics extraction, and IR-drop analysis.' },
      { step: '05', title: 'Tapeout', desc: 'GDSII generation, final DRC/LVS, and foundry submission support.' },
    ],
    whyUs: 'Our ASIC team has experience across TSMC, GlobalFoundries, and Samsung nodes from 180nm to 5nm. We treat every ASIC engagement as a true partnership — from concept through silicon bring-up.',
  },

  'automated-manual-dfm': {
    title: 'Automated & Manual DFM',
    short: 'Design For Manufacturing',
    icon: '🤖',
    color: '#06b6d4',
    banner: 'https://images.unsplash.com/photo-1617788138017-80ad40651399?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Reimagine the Way You Automate DFM',
    overview: `Manufacturing defects discovered after fabrication are expensive. DWB Tech's DFM service combines AI-assisted automated checks with expert manual review to ensure your design is producible, testable, and reliable — before a single board is made.`,
    highlights: [
      'Automated DFM check against 200+ manufacturing rules',
      'Minimum annular ring, acid trap, and solder mask sliver checks',
      'Panelisation design and pick-and-place optimisation',
      'Solder paste aperture review and stencil design',
      'ICT and flying probe test point coverage analysis',
      'BGA pad relief and rework accessibility review',
    ],
    process: [
      { step: '01', title: 'Rule Setup', desc: 'Fabricator DFM rule import and technology definition configuration.' },
      { step: '02', title: 'Auto DFM Run', desc: 'Automated DFM check execution with violation categorisation.' },
      { step: '03', title: 'Manual Review', desc: 'Expert engineer review of all flagged and borderline items.' },
      { step: '04', title: 'Fix Implementation', desc: 'Design corrections applied in native EDA tool with change log.' },
      { step: '05', title: 'DFM Report', desc: 'Comprehensive DFM report with before/after screenshots and sign-off.' },
    ],
    whyUs: "DWB Tech's DFM service has a zero field-escape rate on manufacturing defects. Our dual automated-plus-manual approach catches issues that pure rule checkers miss every time.",
  },

  'voltage-current-analysis': {
    title: 'Voltage & Current Analysis',
    short: 'Power Delivery Analysis',
    icon: '🔋',
    color: '#d97706',
    banner: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=1920&q=85&auto=format&fit=crop',
    tagline: 'Reliable Power Delivery, Every Layer',
    overview: `Power delivery failures are silent killers of digital designs. DWB Tech's voltage and current analysis service delivers a complete DC and AC picture of your board's power integrity — catching problems before they reach production.`,
    highlights: [
      'DC drop analysis across all power domains',
      'Current density mapping and hotspot detection',
      'Via and copper pour current capacity validation',
      'Multi-rail PDN analysis with VRMS and transient loads',
      'Thermal-aware power analysis',
      'Compliance with IPC-2152 current-carrying capacity standards',
    ],
    process: [
      { step: '01', title: 'Netlist Import', desc: 'ODB++ / Gerber import and power net identification.' },
      { step: '02', title: 'Load Modelling', desc: 'Component current consumption modelling and load profile definition.' },
      { step: '03', title: 'DC Analysis', desc: 'IR-drop simulation across all rails with violation flagging.' },
      { step: '04', title: 'Current Density', desc: 'Current density visualisation identifying copper and via bottlenecks.' },
      { step: '05', title: 'Fix & Re-verify', desc: 'Design recommendations implemented and results re-simulated to closure.' },
    ],
    whyUs: 'Leveraging industry-leading power integrity simulation and analysis environments, our team delivers power analysis reports that give both hardware and layout engineers a clear, actionable picture of power integrity risk.',
  },

  'procurement-fab-assembly': {
    title: 'Procurement, Fab & Assembly',
    short: 'End-to-End Supply Chain',
    icon: '🏭',
    color: '#059669',
    banner: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1920&q=85&auto=format&fit=crop',
    tagline: 'From BOM to Boxed Product',
    overview: `DWB Tech's procurement, fabrication, and assembly service closes the loop from design to delivery. We manage your complete supply chain — sourcing components, liaising with certified PCB fabricators, and overseeing board assembly — so you receive tested, box-ready product.`,
    highlights: [
      'Global component sourcing from authorised distributors',
      'BOM optimisation and alternate component selection',
      'IPC Class 2 and Class 3 certified PCB fabrication partners',
      'SMT, through-hole, and mixed-technology assembly',
      'AOI, X-ray, and functional test',
      'Conformal coating, potting, and ruggedisation',
    ],
    process: [
      { step: '01', title: 'BOM Review', desc: 'BOM audit, lifecycle check, and approved alternate identification.' },
      { step: '02', title: 'Sourcing', desc: 'Component procurement from franchised distributors with CoC.' },
      { step: '03', title: 'Fabrication', desc: 'PCB fabrication with IPC Class 3 inspection and test coupons.' },
      { step: '04', title: 'Assembly', desc: 'SMT and through-hole assembly with solder paste inspection and reflow profiling.' },
      { step: '05', title: 'Test & Ship', desc: 'Functional test, conformal coat (if required), and delivery with test report.' },
    ],
    whyUs: 'From prototype quantities of 1 to production runs of 10,000+, DWB Tech manages your supply chain with the same quality standards. Our single-point-of-contact model means faster delivery and clear accountability.',
  },

};

// ── Slug map (title → slug) ────────────────────────────────────────────
export const SERVICE_SLUG_MAP = {
  // PCB & Hardware
  'ATE Board Design':                   'ate-board-design',
  'Probe Card':                         'probe-card',
  'HSD & HDI PCB Designs':             'hsd-hdi-pcb-design',
  'MLO/MLC Substrate Design':          'mlo-mlc-substrate-design',
  'EDA Conversion':                     'eda-conversion',
  'RF Design':                          'rf-design',
  'Library Development':                'library-development',
  'DFM / DFT Analysis':                 'dfm-dft-analysis',
  'Procurement & Assembly':             'procurement-assembly',
  'Signal & Power Integrity':           'signal-power-integrity',
  'CAM Validation and Editing':        'cam-validation-editing',
  // Chip & ASIC
  'ASIC Design Flow':                   'asic-design-flow',
  'Verification Services':              'verification-services',
  // Mechanical Division
  'Turn Key Solution Provider':         'turn-key-solution',
  'Wafer Cassette':                     'wafer-cassette',
  'Pogo Tower':                         'pogo-tower',
  'Interface':                          'interface',
  'Hinge, Manipulator & Cart':          'hinge-manipulator-cart',
  'Test Head Chassis Design & Mfg':     'test-head-chassis',
  'EMS Mobile Assembly':               'ems-mobile-assembly',
  'High Precision Machine Shop':       'high-precision-machine-shop',
  'On Site Service Support (OSS)':     'on-site-service-support',
  // Wiring Harness
  'Semiconductor / EMS Industries':     'semiconductor-ems',
  'Automation / Machine Building':      'automation-machine-building',
  'Defense / Military':                 'defense-military',
  'Aerospace':                          'aero-space',
  'Medical':                            'medical',
  'Telecom':                            'telecom',
  // SMT
  'Fully Automated Assembly':          'fully-automated-assembly',
  '3-Line SMT Support':                'three-line-smt-support',
  '90,000 CPH Capacity':              'smt-cph-capacity',
  // IT Software
  'Web App Development':                'web-app-development',
  'Software Application Dev':           'software-application-dev',
  'Mobile App Development':             'mobile-app-development',
  'Cloud App Development':              'cloud-app-development',
  'Embedded Software Dev':              'embedded-software-dev',
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const svc = SERVICE_DATA[slug];

  if (!svc) {
    return (
      <div className="svc-detail-404">
        <h2>Service not found</h2>
        <Link to="/services">← Back to Services</Link>
      </div>
    );
  }

  return (
    <div className="svc-detail">

      {/* ── Full-width page banner ── */}
      <div className="svc-detail-banner">
        <div
          className="svc-detail-banner-img"
          style={{ backgroundImage: `url('${svc.banner}')` }}
        />
        <div className="svc-detail-banner-overlay" />
        <div className="svc-detail-banner-content">
          <div className="svc-detail-breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/services">Services</Link>
            <span>/</span>
            <span>{svc.title}</span>
          </div>
          <h1 className="svc-detail-banner-title">{svc.tagline}</h1>
          <p className="svc-detail-banner-sub">{svc.short}</p>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="svc-detail-body">
        <div className="svc-detail-inner">

          {/* Left column */}
          <div className="svc-detail-main">

            {/* Overview */}
            <div className="svc-detail-section">
              <div className="svc-detail-section-tag" style={{ color: svc.color }}>Overview</div>
              <h2 className="svc-detail-h2">{svc.title}</h2>
              <p className="svc-detail-overview">{svc.overview}</p>
            </div>

            {/* Highlights */}
            <div className="svc-detail-section">
              <div className="svc-detail-section-tag" style={{ color: svc.color }}>What We Deliver</div>
              <h3 className="svc-detail-h3">Key Capabilities</h3>
              <ul className="svc-detail-list">
                {svc.highlights.map((h, i) => (
                  <li key={i} className="svc-detail-list-item">
                    <span className="svc-detail-check" style={{ background: svc.color }}>✓</span>
                    {h}
                  </li>
                ))}
              </ul>
            </div>

            {/* Process */}
            <div className="svc-detail-section">
              <div className="svc-detail-section-tag" style={{ color: svc.color }}>Our Process</div>
              <h3 className="svc-detail-h3">How We Work</h3>
              <div className="svc-detail-process">
                {svc.process.map((p, i) => (
                  <div key={i} className="svc-detail-step">
                    <div className="svc-detail-step-num" style={{ background: svc.color }}>{p.step}</div>
                    <div className="svc-detail-step-body">
                      <div className="svc-detail-step-title">{p.title}</div>
                      <div className="svc-detail-step-desc">{p.desc}</div>
                    </div>
                    {i < svc.process.length - 1 && <div className="svc-detail-step-line" />}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right sidebar */}
          <aside className="svc-detail-sidebar">

            {/* Why Us card */}
            <div className="svc-detail-why" style={{ borderColor: svc.color }}>
              <div className="svc-detail-why-icon" style={{ background: svc.color }}>{svc.icon}</div>
              <div className="svc-detail-why-title">Why DWB Tech?</div>
              <p className="svc-detail-why-text">{svc.whyUs}</p>
            </div>

            

            {/* Other services */}
            <div className="svc-detail-other">
              <div className="svc-detail-other-title">Other Services</div>
              {Object.entries(SERVICE_SLUG_MAP)
                .filter(([, s]) => s !== slug)
                .slice(0, 5)
                .map(([title, s]) => (
                  <Link key={s} to={`/services/${s}`} className="svc-detail-other-link">
                    <span>→</span> {title}
                  </Link>
                ))}
              <Link to="/services" className="svc-detail-other-all">View All Services →</Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}